import React, { useContext, useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    makeStyles,
    Button,
    Paper, MenuItem,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    DialogActions
} from "@material-ui/core";
import PaddedLayout from "./PaddedLayout";
import PropTypes from 'prop-types';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import StandardFormLayout from './StandardFormLayout';
import LabeledCheckbox from './LabeledCheckbox';
import Fido from '../utils/Fido';
import AuthorizedUserContext from '../utils/AuthorizedUserContext';
import infoIcon from "../assets/info.png";
import step1Icon from "../assets/step-1.png";
import step2Icon from "../assets/step-2.png";
import Select from "./Select";
import Constants from '../utils/Constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import CircularLoadingIndicator from "../components/CircularLoadingIndicator";

const styles = makeStyles(theme => ({
    paper: {
        padding: theme.spacing(5),
    },
    mappingPaper: {
        padding: theme.spacing(5),
        marginTop: "25px"
    },
    paddedLayout: {
        [theme.breakpoints.down('sm')]: {
            paddingTop: theme.spacing(2)
        },
        width: "75%"

    },
    panelHeading: {
        fontWeight: "bold",
        paddingBottom: "15px"
    },
    panelSubHeading: {
        paddingBottom: "45px"
    },
    checkboxHeader: {
        paddingTop: "40px",
        paddingBottom: "20px"
    },
    buttonGrid: {
        marginTop: theme.spacing(2),
        marginLeft: theme.spacing(2),

        "& button": {
            minWidth: theme.spacing(16)
        }
    },
    stepHeader: {
        marginLeft: "-25px"
    }
}));

function GSuiteConfig(props) {
    // const [loadMore, setLoadMore] = useState(false);
    let { showSnackbar } = props;
    const authenticatedUser = useContext(AuthorizedUserContext);
    const [isFirstFormFilled, setIsFirstFormFilled] = useState(false);
    const [additionalSections, setAdditionalSections] = useState([]);
    const [locations, setLocations] = useState([]);
    const [orgUnits, setOrgUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [features, setFeatures] = useState([]);
    const [selectedOrgUnits, setSelectedOrgUnits] = useState([]);
    const nextButton = React.useRef(null);
    const [open, setOpen] = React.useState(false);
    const [gSuiteSettings, setGSuiteSettings] = useState({
        adminUser: '',
        domain: '',
        orgUnit: '',
        gmail: false,
        hangout: false,
        docs: false,
        locationId: 0
    });
    const [hasDomain, setHasDomain] = useState(false);
    let classes = styles();

    // load user settings
    useEffect(() => {
        let fido = new Fido();
        (async function loadUserSettings() {

            let data = await fido.fetch(`/accounts/${authenticatedUser.accountId}/gsuite-settings`)
                .catch(error => {
                    return showSnackbar(error.message);
                });

            if (data) {
                const { adminUser, domain, orgUnitPaths, gmail, hangout, docs } = data;
                const orgUnitPathsKeys = orgUnitPaths ? Object.keys(orgUnitPaths) : {};
                const orgUnit = orgUnitPathsKeys.length > 0 ? orgUnitPathsKeys[0] : '';
                const locationId = orgUnitPaths && orgUnitPaths[orgUnit] && orgUnitPaths[orgUnit].length > 0 ? orgUnitPaths[orgUnit][0] : 0;
                setHasDomain(domain?.length > 0);

                setGSuiteSettings(prevState => ({
                    ...prevState,
                    adminUser: adminUser || authenticatedUser.email,
                    domain,
                    orgUnit,
                    gmail,
                    hangout,
                    docs,
                    locationId
                }));

                manageFeatures(data);
                manageAdditionalSections(orgUnitPaths);
                if (orgUnitPaths) {
                    const keys = Object.keys(orgUnitPaths);
                    setSelectedOrgUnits(keys);
                    nextButton.current.click();
                }
            }
        })();

        return () => fido.dropIt();
    }, [setGSuiteSettings, authenticatedUser, showSnackbar]);
    const manageFeatures = (data) => {
        const featureMapping = {
            gmail: data.gmail,
            hangout: data.hangout,
            docs: data.docs
        };
        const newFeatures = Object.keys(featureMapping).filter(key => featureMapping[key]);
        setFeatures(newFeatures);
    }

    const manageAdditionalSections = (orgUnitPaths) => {
        let additionalSectionsData = [];
        if (orgUnitPaths) {
            additionalSectionsData = Object.entries(orgUnitPaths).slice(1).map(([key, value]) => ({
                orgUnit: key,
                locationId: value[0]
            }));
        }
        setAdditionalSections(additionalSectionsData);

    }
    const updateSettings = (newSettings) => {
        setGSuiteSettings(prevSettings => ({
            ...prevSettings,
            ...newSettings
        }));
    };

    const handleCheckboxChange = (event, field) => {
        const newValue = event.target.checked;
        if (newValue) {
            setFeatures(prevState => [...prevState, field]);
        } else {
            setFeatures(prevState =>
                prevState.filter(item => item !== field)
            );
        }
        updateSettings({ [field]: newValue });
    };

    const handleReset = (event) => {
        let fido = new Fido();
        event.preventDefault();
        const mappings = [];
        setGSuiteSettings({ ...gSuiteSettings, hangout: false, gmail: false, docs: false, orgUnit: '', locationId: '' });
        setAdditionalSections([]);
        setOpen(false);
        const payload = {
            email: gSuiteSettings.adminUser,
            domain: gSuiteSettings.domain,
            features: [],
            mappings: mappings
        };

        fido.fetch(`/accounts/${authenticatedUser.accountId}/gsuite-settings`, {
            method: "POST",
            body: JSON.stringify(payload)
        }).then((data) => {
            showSnackbar("Mapping Saved Successfully");
            return data;
        }).catch(error => showSnackbar(error.message));
    }
    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        let fido = new Fido();
        const payload = {
            orgUnit: gSuiteSettings.orgUnit,
            locationId: gSuiteSettings.locationId,
        };

        try {
            setIsLoading(true);
            const orgUnitsResponse = await fido.fetch(`/accounts/${authenticatedUser.accountId}/ou-paths?domain=${gSuiteSettings.domain}&adminUser=${gSuiteSettings.adminUser}`);
            const locationsResponse = await fido.fetch(`/locations`);

            if (orgUnitsResponse && locationsResponse) {
                const fetchedOrgUnits = orgUnitsResponse.orgUnits || {};
                if (Object.keys(fetchedOrgUnits).length === 0) {
                    throw new Error(orgUnitsResponse.message || 'No organizational units were returned for this domain. Please verify the Admin Email and Domain, then try again.');
                }
                selectedOrgUnits.forEach(item => {
                    if (!fetchedOrgUnits.hasOwnProperty(item)) {
                        fetchedOrgUnits[item] = 0;
                    }
                });
                setOrgUnits(fetchedOrgUnits);
                setLocations(locationsResponse.locations || []);
                setIsFirstFormFilled(true);
            } else {
                throw new Error('Failed to fetch data');
            }
        } catch (error) {
            showSnackbar(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleMappingSubmit = (event) => {
        let fido = new Fido();
        event.preventDefault();
        const mappings = [];
        const mainOrgUnit = gSuiteSettings.orgUnit;
        const mainLocationId = gSuiteSettings.locationId;

        mappings.push({ orgUnit: mainOrgUnit, locationId: mainLocationId });

        additionalSections.forEach(section => {
            const { orgUnit, locationId } = section;
            mappings.push({ orgUnit, locationId });
        });
        const payload = {
            email: gSuiteSettings.adminUser,
            domain: gSuiteSettings.domain,
            features: features,
            mappings: mappings
        };
        fido.fetch(`/accounts/${authenticatedUser.accountId}/gsuite-settings`, {
            method: "POST",
            body: JSON.stringify(payload)
        }).then((data) => {
            showSnackbar("Mapping Saved Successfully");
            return data;
        }).catch(error => showSnackbar(error.message));
    }
    const handleAddSection = () => {
        setAdditionalSections([...additionalSections, { orgUnit: '', locationId: '' }]);
    };

    const handleOrgUnitChange = (index, value) => {
        if (selectedOrgUnits.includes(value)) {
            showSnackbar("The selected organizational unit is already in use. Please select other value");
            return;
        }
        const updatedSelectedOrgUnits = [...selectedOrgUnits, value];
        setSelectedOrgUnits(updatedSelectedOrgUnits);
        const newSections = [...additionalSections];
        newSections[index].orgUnit = value;
        setAdditionalSections(newSections);
    };

    const handleLocationIdChange = (index, value) => {
        const newSections = [...additionalSections];
        newSections[index].locationId = value;
        setAdditionalSections(newSections);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        if (name === 'orgUnit') {
            if (selectedOrgUnits.includes(value)) {
                showSnackbar("The selected organizational unit is already in use. Please select other value");
                return;
            }
            const updatedSelectedOrgUnits = [...selectedOrgUnits, value];
            setSelectedOrgUnits(updatedSelectedOrgUnits);
            updateSettings({ orgUnit: value });
        } else if (name === 'locationId') {
            updateSettings({ locationId: value });
        }
    };

    const hasUserAndDomainValues = () => {
        return !gSuiteSettings.adminUser || !gSuiteSettings.domain
    }

    const disableAddUnit = () => {
        return (additionalSections.length + 1) === Object.keys(orgUnits || {}).length
    }

    const handleRemoveSection = (indexToRemove, section) => {
        setAdditionalSections(prevSections => {
            const updatedSections = prevSections.filter((section, index) => index !== indexToRemove);
            return updatedSections;
        });
        const updatedSelectedOrgUnits = selectedOrgUnits.filter(unit => unit !== section.orgUnit);
        setSelectedOrgUnits(updatedSelectedOrgUnits);
    };

    const disableSubmitButton = () => {
        return (!gSuiteSettings.orgUnit || !gSuiteSettings.locationId) || additionalSections.some(section => !section.orgUnit || !section.locationId);
    }

    return (
        <PaddedLayout className={classes.paddedLayout}>
            <Typography className={classes.panelHeading}>Source configuration</Typography>
            <Typography className={classes.panelSubHeading}>Begin by entering details for the Organizational Unit and choosing your preferred GSuite Features. The fields for Admin Email and domain will be pre-populated for your convenience. In the second step, you will be able to associate the ID with the Organizational Unit.</Typography>
            <Paper className={classes.paper} elevation={2}>
                <Grid container alignItems="center" className={classes.stepHeader}>
                    <Grid item>
                        <img src={step1Icon} alt='step1' style={{ marginRight: '20px' }} />
                    </Grid>
                    <Grid item>
                        <Typography className={classes.panelHeading}>Configure your Google Account <img src={infoIcon} alt='info' /></Typography>
                    </Grid>
                </Grid>
                <Grid container justify="center">
                    <Grid item xs={11}>
                        <ValidatorForm
                            onSubmit={handleSubmit}
                            debounceTime={500}>
                            <StandardFormLayout>
                                <TextValidator
                                    fullWidth
                                    className='test-mui-class'
                                    id="adminUser"
                                    label="Admin Email"
                                    name="admin_email"
                                    helperText="Admin Email"
                                    value={gSuiteSettings.adminUser}
                                    margin="normal"
                                    validators={['required', 'isEmail']}
                                    errorMessages={[Constants.ERROR_MESSAGES['required'], Constants.ERROR_MESSAGES['isEmail']]}
                                    disabled={true}
                                />
                                <TextValidator
                                    fullWidth
                                    id="domain"
                                    label="Domain"
                                    name="domain"
                                    helperText="Domain"
                                    value={gSuiteSettings.domain}
                                    margin="normal"
                                    validators={['required']}
                                    errorMessages={[Constants.ERROR_MESSAGES['required']]}
                                    hintText={`Domain`}
                                    disabled={hasDomain}
                                    onChange={e => updateSettings({ domain: e.target.value })}
                                />
                                <Typography className={classes.checkboxHeader}> Gsuite feature (Select all applicable).</Typography>
                                <LabeledCheckbox
                                    id="gmail"
                                    name="gmail"
                                    label="Gmail"
                                    checked={gSuiteSettings.gmail}
                                    onChange={e => handleCheckboxChange(e, 'gmail')}
                                    checkboxColor="primary"
                                />
                                <LabeledCheckbox
                                    id="hangout"
                                    name="hangout"
                                    label="Chat"
                                    checked={gSuiteSettings.hangout}
                                    onChange={e => handleCheckboxChange(e, 'hangout')}
                                    checkboxColor="primary"
                                />
                                <LabeledCheckbox
                                    id="docs"
                                    name="docs"
                                    label="Docs"
                                    checked={gSuiteSettings.docs}
                                    onChange={e => handleCheckboxChange(e, 'docs')}
                                    checkboxColor="primary"
                                />
                                <Grid container justify="flex-end" className={classes.buttonGrid}>
                                    <Grid item>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            type="button"
                                            style={{ marginRight: "10px" }}
                                            onClick={handleClickOpen}>Reset</Button>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={hasUserAndDomainValues()}
                                            ref={nextButton}
                                            onClick={handleSubmit}>NEXT STEP</Button>
                                    </Grid>
                                </Grid>
                            </StandardFormLayout>
                        </ValidatorForm>
                    </Grid>
                </Grid>
            </Paper>
            {isLoading && <CircularLoadingIndicator />}
            <div style={{ marginTop: '20px' }}></div>
            {isFirstFormFilled && !isLoading && <Paper className={classes.paper} elevation={2}>
                <Grid container alignItems="center" className={classes.stepHeader}>
                    <Grid item>
                        <img src={step2Icon} alt='step2' style={{ marginRight: '20px' }} />
                    </Grid>
                    <Grid item>
                        <Typography className={classes.panelHeading}>Retrieve ID Mapping <img src={infoIcon} alt='info' /></Typography>
                    </Grid>
                </Grid>
                <Grid container justify="center">
                    <Grid item xs={11}>
                        <ValidatorForm
                            onSubmit={handleMappingSubmit}
                            debounceTime={500}>
                            <StandardFormLayout>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={6}>
                                        <Select
                                            isLoading={false}
                                            id="orgUnit"
                                            noValidator={false}
                                            name="orgUnit"
                                            label="Organizational Unit"
                                            helperText="Organizational Unit"
                                            margin="normal"
                                            value={gSuiteSettings.orgUnit}
                                            onChange={handleChange}
                                            style={{ textAlign: "start" }}
                                            disabled={hasUserAndDomainValues()}
                                            validators={['required']}
                                            errorMessages={[Constants.ERROR_MESSAGES['required']]}>
                                            {Object.keys(orgUnits || {}).map(unit =>
                                                <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                            )}

                                        </Select>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Select
                                            isLoading={false}
                                            id="locationId"
                                            noValidator={false}
                                            name="locationId"
                                            label="Location ID"
                                            helperText="Location ID"
                                            margin="normal"
                                            value={gSuiteSettings.locationId}
                                            onChange={handleChange}
                                            style={{ textAlign: "start" }}
                                            disabled={hasUserAndDomainValues()}
                                            validators={['required']}
                                            errorMessages={[Constants.ERROR_MESSAGES['required']]}>
                                            {locations.map(location =>
                                                <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                                            )}
                                        </Select>

                                    </Grid>
                                </Grid>
                                {additionalSections.map((section, index) => (
                                    <Grid item xs={12} key={index}>
                                        <Grid container spacing={2} style={{ alignItems: 'center' }}>
                                            <Grid item xs={6}>
                                                <Select
                                                    isLoading={false}
                                                    id={`orgUnit-${index}`}
                                                    name={`orgUnit-${index}`}
                                                    label="Organizational Unit"
                                                    helperText="Organizational Unit"
                                                    margin="normal"
                                                    value={section.orgUnit}
                                                    onChange={(event) => handleOrgUnitChange(index, event.target.value)}
                                                    style={{ textAlign: "start" }}
                                                    validators={['required']}
                                                    errorMessages={['This field is required']}
                                                >
                                                    {Object.keys(orgUnits || {}).map(unit =>
                                                        <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                                                    )}
                                                </Select>
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Select
                                                    isLoading={false}
                                                    id={`locationId-${index}`}
                                                    name={`locationId-${index}`}
                                                    label="Location ID"
                                                    helperText="Location ID"
                                                    margin="normal"
                                                    value={section.locationId}
                                                    onChange={(event) => handleLocationIdChange(index, event.target.value)}
                                                    style={{ textAlign: "start" }}
                                                    validators={['required']}
                                                    errorMessages={['This field is required']}
                                                >
                                                    {locations.map(location =>
                                                        <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>
                                                    )}
                                                </Select>
                                            </Grid>
                                            <Grid item xs={1} style={{ display: 'flex', cursor: 'pointer', paddingTop: '40px', justifyContent: 'center' }}>
                                                <FontAwesomeIcon icon={faTrashAlt} size='lg' onClick={() => handleRemoveSection(index, section)} />
                                            </Grid>
                                        </Grid>
                                    </Grid>))}

                                <Grid item disabled={disableAddUnit()}>
                                    <Button variant="text" style={{ color: "#20C085", marginTop: 20 }} onClick={handleAddSection} disabled={disableAddUnit()}>Add Unit +</Button>
                                </Grid>

                                <Grid container justify="flex-end" className={classes.buttonGrid}>
                                    <Grid item>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={disableSubmitButton()}
                                            type='submit'>SUBMIT</Button>
                                    </Grid>
                                </Grid>
                            </StandardFormLayout>
                        </ValidatorForm>
                    </Grid>
                </Grid>
            </Paper>}
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {"Confirm Reset"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to reset your GSuite configuration? Doing so will halt ingestion and require the configured administrator to re-enable the integration. If you need to adjust your mappings, you can do so below without a hard reset.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={handleReset} autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </PaddedLayout>
    );
}

GSuiteConfig.defaultProps = {
    maxComments: 3
};

GSuiteConfig.propTypes = {
    anchorText: PropTypes.string,
    priorComments: PropTypes.array,
    content: PropTypes.string,
    time: PropTypes.string,
    authorName: PropTypes.string,
    libraryTopics: PropTypes.array,
    maxComments: PropTypes.number,
    hasImage: PropTypes.bool,
    sourceIcon: PropTypes.object
};

export default GSuiteConfig;